import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/ja";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  BASIC_BLACK,
  BASIC_DARK_GRAY,
  BASIC_WHITE,
} from "../../constants/colors";
import IconCalendar from "../../icons/IconCalendar";
import { Calendar } from "./Calendar";

dayjs.locale("ja");

type Props = {
  name?: string;
  value?: Date | Dayjs | string;
  minDate?: Date | Dayjs | string;
  maxDate?: Date | Dayjs | string;
  displayFormat?: string;
  onChange: (date: Dayjs) => void;
};

const ANIMATION_DURATION = 200;

export const DatePicker: React.FC<Props> = ({
  name,
  value,
  minDate,
  maxDate,
  displayFormat,
  onChange,
}) => {
  const [selectedValue, setSelectedValue] = useState<Dayjs | null>(
    value ? dayjs(value) : null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelect = (newValue: Dayjs) => {
    handleClose();
    setSelectedValue(newValue);
    onChange(newValue);
  };

  const handleOpen = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsOpen(false);
    }, ANIMATION_DURATION);
  };

  const portalContainer = useMemo(() => {
    if (typeof document === "undefined") return null;

    let container = document.getElementById("datepicker-portal");
    if (!container) {
      container = document.createElement("div");
      container.id = "datepicker-portal";
      container.style.position = "absolute";
      container.style.top = "0";
      container.style.left = "0";
      container.style.width = "100%";
      container.style.height = "0";
      container.style.pointerEvents = "none";
      container.style.zIndex = "9996";
      document.body.appendChild(container);
    }
    return container;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (portalContainer && !portalContainer.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, portalContainer]);

  useEffect(() => {
    if (isOpen && portalContainer && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      portalContainer.style.position = "absolute";
      portalContainer.style.top = `${rect.bottom + window.scrollY + 10}px`;
      portalContainer.style.left = `${rect.left + window.scrollX}px`;
      portalContainer.style.width = `${rect.width}px`;
      portalContainer.style.pointerEvents = "auto";
    } else if (portalContainer) {
      portalContainer.style.pointerEvents = "none";
    }
  }, [isOpen, portalContainer]);

  useEffect(() => {
    return () => {
      if (portalContainer && portalContainer.parentNode) {
        portalContainer.parentNode.removeChild(portalContainer);
      }
    };
  }, [portalContainer]);

  const renderPortalDropdown = useCallback(() => {
    if (!isOpen || !portalContainer) return null;

    return createPortal(
      <AnimatedCalendarContainer $isAnimating={isAnimating}>
        <Calendar
          value={selectedValue ?? new Date()}
          onConfirm={handleSelect}
          onClose={handleClose}
          minDate={minDate}
          maxDate={maxDate}
        />
      </AnimatedCalendarContainer>,
      portalContainer,
    );
  }, [
    isOpen,
    portalContainer,
    selectedValue,
    handleSelect,
    handleClose,
    minDate,
    maxDate,
    isAnimating,
  ]);

  return (
    <>
      <Container ref={containerRef} onClick={handleOpen}>
        <StyledDisplayContainer $hasIcon={true}>
          <StyledDisplayDate
            name={name}
            value={
              selectedValue
                ? selectedValue.format(displayFormat ?? "YYYY年M月D日")
                : "日付を選択してください"
            }
            readOnly
          />
          <IconCalendar />
        </StyledDisplayContainer>
      </Container>
      {renderPortalDropdown()}
    </>
  );
};

const Container = styled.div`
  position: relative;
  border: solid 1px ${BASIC_DARK_GRAY};
  margin: 0;
  cursor: pointer;
  background-color: ${BASIC_WHITE};
  color: ${BASIC_BLACK};
  transition: border-color 0.2s ease-in-out;

  &:hover {
    border-color: ${BASIC_BLACK};
  }
`;

const StyledDisplayContainer = styled.div<{ $hasIcon: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 14px 16px;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-1px);
  }
`;

const StyledDisplayDate = styled.input`
  font-family: Hiragino Sans;
  font-weight: 600;
  font-size: 16px;
  line-height: 2em;
  letter-spacing: 0%;
  color: ${BASIC_BLACK};
  background-color: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  text-align: left;
  padding: 0;
  margin: 0;
  transition: color 0.2s ease-in-out;
`;

const AnimatedCalendarContainer = styled.div<{ $isAnimating: boolean }>`
  animation: ${(props) => (props.$isAnimating ? slideDownIn : slideUpOut)}
    ${ANIMATION_DURATION}ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  transform-origin: top center;
  will-change: opacity, transform;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15));
`;

const slideDownIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideUpOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-16px) scale(0.98);
  }
`;
